<template>
    <BaseLink
        v-if="props.game"
        :class="$style.container"
        v-bind="linkData"
    >
        <div
            ref="container"
            :class="['sqrcl', $style.containerCard]"
        >
            <CardContainer
                appearance="vertical"
                :class="$style.gameCard_override"
                :alt="additionalGameData.title"
                :src="art?.image"
                responsive
                lazy
                :sqrcl="false"
                itemscope
                itemtype="https://schema.org/VideoGame"
                itemprop="image"
                data-interface-target="game"
                @mouseenter="setMouseOver"
                @mouseleave="setMouseOut"
                @focus="setMouseOver"
                @blur="setMouseOut"
            >
                <svg
                    v-if="props.game && isMounted"
                    id="svgTitle"
                    viewBox="0 0 220 391"
                    xmlns="http://www.w3.org/2000/svg"
                    :data-pg-top="true"
                    data-allow-mismatch
                    :class="[$style.svg, isAutoBlinking ? $style.svgOnAutoBlink : '', 'lazyload']"
                >
                    <title>{{ additionalGameData.title }}</title>
                    <defs>
                        <filter :id="`${svgNamespace}-brightnessFilter`">
                            <feColorMatrix
                                in="SourceGraphic"
                                type="luminanceToAlpha"
                                result="luma"
                            />

                            <feComponentTransfer
                                in="luma"
                                result="mask"
                            >
                                <feFuncA
                                    ref="wide-func-a"
                                    type="table"
                                    tableValues="0"
                                />
                            </feComponentTransfer>

                            <feFlood
                                :flood-color="art?.color"
                                result="color"
                            />

                            <feComposite
                                in="color"
                                in2="mask"
                                operator="in"
                                result="colored"
                            />

                            <!--WHITE thin-->
                            <feComponentTransfer
                                in="luma"
                                result="mask-thin"
                            >
                                <feFuncA
                                    ref="thin-func-a"
                                    type="table"
                                    tableValues="0"
                                />
                            </feComponentTransfer>

                            <feFlood
                                flood-color="#ffffff88"
                                result="color"
                            />
                            <feComposite
                                in="color"
                                in2="mask-thin"
                                operator="in"
                                result="white-thin"
                            />

                            <feColorMatrix
                                in="white-thin"
                                type="matrix"
                                values="
          1 0 0 0 0
          0 1 0 0 0
          0 0 1 0 0
          0 0 0 1.8 0"
                                result="white-thin-glowSource"
                            />

                            <feGaussianBlur
                                in="white-thin-glowSource"
                                stdDeviation="1.3"
                                result="white-thin-blurredGlow"
                            />

                            <!--WHITE thin end-->

                            <feColorMatrix
                                in="colored"
                                type="matrix"
                                values="
          1 0 0 0 0
          0 1 0 0 0
          0 0 1 0 0
          0 0 0 1.8 0"
                                result="glowSource"
                            />

                            <feGaussianBlur
                                in="glowSource"
                                stdDeviation="11"
                                result="blurredGlow"
                            />

                            <feMerge>
                                <feMergeNode in="blurredGlow" />
                                <feMergeNode in="white-thin-blurredGlow" />
                            </feMerge>
                        </filter>
                    </defs>
                    <image
                        :data-href="art?.image"
                        width="100%"
                        height="100%"
                        preserveAspectRatio="xMidYMid slice"
                    />
                    <image
                        :data-href="art?.fod"
                        width="100%"
                        height="100%"
                        :filter="`url(#${svgNamespace}-brightnessFilter)`"
                        preserveAspectRatio="xMidYMid slice"
                    />
                </svg>
            </CardContainer>

            <GameCardTitleWithIcon
                v-if="props.game"
                :icon="additionalGameData.icon"
                :likes="additionalGameData.likes"
                :title="additionalGameData.title"
            />
        </div>
        <Badge
            v-if="props.game"
            :badges="additionalGameData.badges"
            :isHide="isMouseOver"
        />
    </BaseLink>
</template>

<script setup lang="ts">
import { useElementVisibility } from '@vueuse/core'
import { computed, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue'

import Badge from '@/components/Badge/Badge.vue'
import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import type { LinkTarget, ShortGameType } from '@/types'
import { formatValue } from '@/utils/numbers'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import { acquireAutoBlinkSlot, dequeueAutoBlink, enqueueAutoBlink, releaseAutoBlinkSlot } from './auto-blink-queue'
import GameCardTitleWithIcon from './GameCardTitleWithIcon.vue'
import { topPlaygamaArt } from './images'

const props = defineProps<{
    game?: ShortGameType
    gameLinkTarget: LinkTarget | undefined
}>()

const appStore = useAppStore()

const getExternalUrl = useGetExternalUrl()

const isMounted = useIsMounted()
const art = computed(() => topPlaygamaArt[props.game?.hru as string])
const additionalGameData = computed(() => ({
    likes: formatValue(props.game?.ratingCount, { shorten: true }),
    icon: props.game?.icon ?? '',
    title: props.game?.title ?? '',
    badges: props.game?.badges ?? [],
}))

const linkData = computed(() => {
    const to = props.game && { name: ROUTE_NAMES.GAME_PAGE, params: { game: props.game.hru } }
    return appStore.isPWA ? { to } : { externalTo: to && getExternalUrl(to), target: props.gameLinkTarget }
})

const containerRef = useTemplateRef('container')
const wideFuncARef = useTemplateRef('wide-func-a')
const thinFuncARef = useTemplateRef('thin-func-a')

const isVisible = useElementVisibility(containerRef, { threshold: 0.5 })

const generateTableValues = (centerSize: number = 5, y?: number): string => {
    const size = 100
    const yPosition = 1 - (y ?? 0.5)
    const clampedPosition = Math.max(0, Math.min(1, yPosition))
    const peakCenter = clampedPosition * (size - 1)
    const resultArray: number[] = new Array(size).fill(0)

    const min = Math.floor(Math.max(peakCenter - centerSize, 0))
    const max = Math.floor(Math.min(peakCenter + centerSize, size - 1))
    for (let i = min; i < max; i++) {
        resultArray[i] = 1
    }
    return resultArray.join(' ')
}

const AUTO_BLINK_SPEED = 1.05 // position units per second

const svgNamespace = useId()
const isMouseOver = ref(false)
let autoBlickPosition = 0
let autoBlinkDirection = 1
let lastFrameTime: number | undefined
let rafId: number | undefined
const isAutoBlinking = ref(false)
let hoverRect: DOMRect | undefined
let queuedAutoBlinkStart: (() => void) | undefined

function cancelQueuedAutoBlink() {
    if (queuedAutoBlinkStart) {
        dequeueAutoBlink(queuedAutoBlinkStart)
        queuedAutoBlinkStart = undefined
    }
}

function mouseMove(e: MouseEvent) {
    if (rafId !== undefined || !hoverRect) {
        return
    }
    const position = ((e.clientY - hoverRect.top) / hoverRect.height) * 0.8
    wideFuncARef.value?.setAttribute('tableValues', generateTableValues(10, position))
    thinFuncARef.value?.setAttribute('tableValues', generateTableValues(1, position))
}

function updateHoverRect() {
    hoverRect = containerRef.value?.getBoundingClientRect()
}

function setMouseOver() {
    isMouseOver.value = true
    updateHoverRect()
    containerRef.value?.addEventListener('mousemove', mouseMove)
    window.addEventListener('scroll', updateHoverRect, { passive: true })
}

function setMouseOut() {
    isMouseOver.value = false
    containerRef.value?.removeEventListener('mousemove', mouseMove)
    window.removeEventListener('scroll', updateHoverRect)
}

function startAutoBlink() {
    if (rafId !== undefined) {
        return
    }
    if (!acquireAutoBlinkSlot()) {
        cancelQueuedAutoBlink()
        queuedAutoBlinkStart = startAutoBlink
        enqueueAutoBlink(queuedAutoBlinkStart)
        return
    }

    queuedAutoBlinkStart = undefined
    autoBlickPosition = 0
    autoBlinkDirection = 1
    lastFrameTime = undefined
    isAutoBlinking.value = true

    const animate = (time: number) => {
        const dt = lastFrameTime === undefined ? 0 : Math.min((time - lastFrameTime) / 1000, 0.1)
        lastFrameTime = time

        autoBlickPosition += AUTO_BLINK_SPEED * autoBlinkDirection * dt
        if (autoBlickPosition >= 0.8) {
            autoBlinkDirection = -1
        } else if (autoBlickPosition <= 0 && autoBlinkDirection < 0) {
            wideFuncARef.value?.setAttribute('tableValues', '0')
            thinFuncARef.value?.setAttribute('tableValues', '0')
            rafId = undefined
            isAutoBlinking.value = false
            releaseAutoBlinkSlot()
            return
        }

        wideFuncARef.value?.setAttribute('tableValues', generateTableValues(10, autoBlickPosition))
        thinFuncARef.value?.setAttribute('tableValues', generateTableValues(1, autoBlickPosition))

        rafId = requestAnimationFrame(animate)
    }

    rafId = requestAnimationFrame(animate)
}

function stopAutoBlink() {
    cancelQueuedAutoBlink()
    if (rafId !== undefined) {
        cancelAnimationFrame(rafId)
        rafId = undefined
        isAutoBlinking.value = false
        wideFuncARef.value?.setAttribute('tableValues', '0')
        thinFuncARef.value?.setAttribute('tableValues', '0')
        releaseAutoBlinkSlot()
    }
}

onUnmounted(() => {
    stopAutoBlink()
    setMouseOut()
})

watch(isVisible, (newValue) => {
    if (newValue && window?.innerWidth <= 1200) {
        startAutoBlink()
    } else {
        stopAutoBlink()
    }
})
</script>

<style module>
.svg {
    grid-area: main;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition:
        opacity 0.3s ease,
        transform 0.5s ease;
}

.svgOnAutoBlink {
    opacity: 1;
}

.gameCard_override {
    transition: transform 0.5s ease;
    transform: scale(1);
}

.container {
    position: relative;
}

.containerCard {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    --sqrcl: clamp(0rem, 30%, 50rem);
}

.containerCard:hover .gameCard_override {
    transform: scale(1.03);
}

.containerCard:hover .svg {
    opacity: 1;
    transition: transform 0.5s ease;
    transform: scale(1.03);
}
</style>
